/**
 * Design Your Own API - Manages the DYO flow state and data
 * 
 * Examples:
 * 
 * Set a setting
 * 
 * window.theme.dyo.setSetting({
 *   id: 'setting-1',
 *   title: 'Twisted Infinity Round Diamond Engagement Ring',
 *   price: '$1,306',
 *   image: 'https://placehold.co/100x100/e2e8f0/64748b?text=Setting+1',
 *   sku: 'SET-001'
 * });
 * 
 * theme.dyo.setSetting(theme.dyo.constructor.getSampleData().settings[0])
 * 
 * Set a diamond
 * 
 * theme.dyo.setDiamond(theme.dyo.constructor.getSampleData().diamonds[0])
 * 
 * 
 * @module dyo-api
 * @version 1.0.0
 */

if (!window.theme.dyo) {

  class DYOApi {
    
    constructor() {
      
      this.steps = [
        { id: 'settings', name: 'Pick Setting', status: 'pending' },
        { id: 'diamonds', name: 'Pick Stone', status: 'pending' },
        { id: 'review', name: 'Review Your Ring', status: 'pending' }
      ];
      
      this.data = {
        setting: null,
        diamond: null,
        createdProduct: null  // { handle, url }
      };

      // Store the initial step from which the user started the DYO flow
      // This is used to determine the order of steps in the DYO bar
      // 'settings' or 'diamonds'
      this.startedWith = '';

      // Store the next step
      // 'settings','diamonds' or 'review'
      this.nextStep = '';

      this.listeners = [];

      // Cache for the last generated short link { stateKey, code }
      this._shortLinkCache = null;

      // Initialize localStorage if needed
      this.loadFromStorage();

      // Notify body tag that the DYO API is ready
      document.body.dispatchEvent(new CustomEvent('dyo-api-ready', { detail: this.data }));
    }
    
    /**
     * Get all steps with their current status
     */
    getSteps() {
      return this.steps.map(step => ({
        ...step,
        data: this.data[step.id]
      }));
    }
    
    /**
     * Set setting data and advance to next step
     */
    setSetting(settingData) {

      if (!this.data.diamond) {
        this.startedWith = 'settings';
        this.nextStep = 'diamonds';
      } else {
        this.nextStep = 'review';
      }
      
      this.data.setting = {
        id: settingData.id,
        handle: settingData.handle,
        title: settingData.title,
        price: settingData.price,
        totalPrice: this.getSettingFinalPrice(settingData),
        image: settingData.image,
        images: settingData.images || [],
        sku: settingData.sku || null,
        size: settingData.size || null,
        engraving: settingData.engraving || null,
        fontStyle: settingData.fontStyle || null,
        warranty: settingData.warranty || null,
        appraisal: settingData.appraisal || null,
        metafields: settingData.metafields || {}
      };
      
      // Mark current step as completed
      this.steps[0].status = 'completed';
      
      this.saveToStorage();
      this.notifyListeners('setting-selected', this.data.setting);
    }
    
    /**
     * Set diamond data and advance to review step
     */
    async setDiamond(diamondData) {

      if (!this.data.setting) {
        this.startedWith = 'diamonds';
        this.nextStep = 'settings';
      } else {
        this.nextStep = 'review';
      }

      // TODO: Think in a way to get the product type from the diamond data to expand it to other product types like earrings.
      const compatibleCollectionHandle = await this.getCompatibleSettingCollectionHandle('Engagement Ring', diamondData.shape, diamondData.carat);

      this.data.diamond = {
        id: diamondData.id,
        title: diamondData.title,
        price: diamondData.price,
        totalPrice: diamondData.price,
        image: diamondData.image,
        sku: diamondData.sku || null,
        carat: diamondData.carat || null,
        color: diamondData.color || null,
        clarity: diamondData.clarity || null,
        cut: diamondData.cut || null,
        shape: diamondData.shape || null,
        compatibleCollectionHandle: compatibleCollectionHandle.data.handle || null,
        type: diamondData.type.toLowerCase() || null,
        source: diamondData.source || 'legacy',
        handle: diamondData.handle || null,
        variantId: diamondData.variantId || null
      };
      
      // Mark diamonds step as completed
      this.steps[1].status = 'completed';
      
      this.saveToStorage();
      this.notifyListeners('diamond-selected', this.data.diamond);

      return this.data.diamond;
      
      // Auto-navigate to review page if both setting and diamond are selected
      // if (this.isComplete()) {
      //   this.navigateToReview();
      // }
    }

    /**
     * Get the collection handle for a given product type, shape, and carat for compatible settings
     * 
     * @param {string} productType  Product type (e.g. "Engagement Ring")
     * @param {string} shape  Shape (e.g. "Round")
     * @param {string} carat  Carat weight (e.g. "0.18")
     * 
     * @example
     * 
     * getCompatibleSettingCollectionHandle("Engagement Ring", "Round", "0.18")
     * 
     * @returns Collection handle for compatible settings
     */
    async getCompatibleSettingCollectionHandle(productType, shape, carat) {
      
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const caratWeight = (parseFloat(carat) + 0.01).toFixed(2).toString();

      const caratWeightMetafieldDefinition = `custom.build_${shape.toLowerCase()}_min_carat_weight`;

      const raw = JSON.stringify({
        "name": `DYO - ${productType} - ${shape} - Carat Weight - Min ${carat}`,
        "rules": [
          {
            "column": "PRODUCT_METAFIELD_DEFINITION",
            "metafieldDefinition": "seo.hidden",
            "relation": "LESS_THAN",
            "condition": "1"
          },
          {
            "column": "PRODUCT_METAFIELD_DEFINITION",
            "metafieldDefinition": "custom.is_build_product",
            "relation": "EQUALS",
            "condition": "Yes"
          },
          {
            "column": "PRODUCT_METAFIELD_DEFINITION",
            "metafieldDefinition": "custom.product_type",
            "relation": "EQUALS",
            "condition": productType
          },
          {
            "column": "PRODUCT_METAFIELD_DEFINITION",
            "metafieldDefinition": caratWeightMetafieldDefinition,
            "relation": "LESS_THAN",
            "condition": caratWeight
          }
        ]
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };
      
      const response = await fetch("https://app.allurez.com/api/index-collections", requestOptions);

      const data = await response.json();

      return data;
    }
    
    /**
     * Go back to edit a specific step
     */
    editStep(stepId) {
      
      const stepIndex = this.steps.findIndex(step => step.id === stepId);
      if (stepIndex === -1) return false;
      
      // Special handling for editing settings step
      if (stepId === 'settings') {

        // Reset setting selection
        this.data.setting = null;
        this.data.createdProduct = null;
        this.steps[0].status = 'pending';
        this.steps[2].status = 'pending'; // review step

        if (this.data.diamond) {
          this.nextStep = 'settings';
        }
        
        this.saveToStorage();
        this.notifyListeners('setting-reset', null);

        return true;
      }
      
      // Special handling for editing diamonds step
      if (stepId === 'diamonds') {

        // Reset diamond selection
        this.data.diamond = null;
        this.data.createdProduct = null;
        this.steps[1].status = 'pending'; // diamonds step becomes active
        this.steps[2].status = 'pending'; // review step becomes pending

        if (this.data.setting) {
          this.nextStep = 'diamonds';
        }
        
        this.saveToStorage();
        this.notifyListeners('diamond-reset', null);
        return true;
      }

      // If no diamond or setting is selected, reset the startedWith attribute
      if (!this.data.diamond && !this.data.setting) {
        this.reset();
      }

      return true;
    }
    
    /**
     * Get the total price of selected items
     */
    getTotalPrice() {
      let total = 0;
      if (this.data.setting && this.data.setting.price) {
        total += parseFloat(this.data.setting.price.replace(/[$,]/g, ''));
      }
      if (this.data.diamond && this.data.diamond.price) {
        total += parseFloat(this.data.diamond.price.replace(/[$,]/g, ''));
      }
      return total;
    }
    
    /**
     * Check if configuration is complete
     */
    isComplete() {
      return this.data.setting && this.data.diamond;
    }
    
    /**
     * Navigate to the custom ring review page with shareable URL
     */
    navigateToReview() {
      if (!this.isComplete()) {
        console.warn('Cannot navigate to review: DYO configuration is incomplete');
        return false;
      }

      // Check if we're already on the review page
      if (window.location.pathname === '/products/custom-ring') {
        return true;
      }

      // Navigate to review page with encoded state so the URL is immediately shareable
      window.location.href = this.getShareableUrl();
      return true;
    }

    /**
     * Returns the shareable review URL with the current DYO state encoded as a URL parameter.
     * The URL can be copied, saved, and used for retargeting.
     *
     * @returns {string} Full path to the review page with ?dyo= parameter
     */
    getShareableUrl() {
      try {
        const state = {
          setting: this.data.setting,
          diamond: this.data.diamond,
          steps: this.steps,
          startedWith: this.startedWith,
          nextStep: this.nextStep
        };
        const encoded = btoa(encodeURIComponent(JSON.stringify(state)));
        return `/products/custom-ring?dyo=${encoded}`;
      } catch (e) {
        console.warn('Could not encode DYO state for URL:', e);
        return '/products/custom-ring';
      }
    }

    /**
     * Stores the current DYO state via the API and returns a short shareable URL.
     *
     * @returns {Promise<string>} Path to the review page with an 8-char ?dyo= code
     */
    async generateShortUrl() {
      const state = {
        setting: this.data.setting,
        diamond: this.data.diamond,
        steps: this.steps,
        startedWith: this.startedWith,
        nextStep: this.nextStep
      };

      const stateKey = JSON.stringify(state);
      if (this._shortLinkCache && this._shortLinkCache.stateKey === stateKey) {
        return `/products/custom-ring?dyo=${this._shortLinkCache.code}`;
      }

      const response = await fetch('https://app.allurez.com/api/dyo-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ data: state })
      });

      const result = await response.json();
      if (!result.success) throw new Error('Failed to generate short link');

      this._shortLinkCache = { stateKey, code: result.code };
      return `/products/custom-ring?dyo=${result.code}`;
    }

    /**
     * Load DYO state from the current page's ?dyo= URL parameter.
     * Supports both 8-character short codes (fetched from API) and legacy base64 strings.
     * Overrides localStorage state, enabling shared/retargeted URLs to work.
     *
     * @returns {Promise<boolean>} True if state was successfully loaded from URL
     */
    async loadFromUrl() {
      try {
        const encoded = new URLSearchParams(window.location.search).get('dyo');
        if (!encoded) return false;

        let state;

        if (encoded.length <= 8) {
          // Short code — fetch full config from API
          const response = await fetch(`https://app.allurez.com/api/dyo-links/${encoded}`, {
            headers: { 'Accept': 'application/json' }
          });
          if (!response.ok) return false;
          const result = await response.json();
          if (!result.success) return false;
          state = result.data;
        } else {
          // Legacy base64 — decode locally
          state = JSON.parse(decodeURIComponent(atob(encoded)));
        }

        if (!state || (!state.setting && !state.diamond)) return false;

        this.data = {
          setting: state.setting || null,
          diamond: state.diamond || null,
          createdProduct: state.createdProduct || null
        };
        this.steps = state.steps || this.steps;
        this.startedWith = state.startedWith || '';
        this.nextStep = state.nextStep || '';

        // Persist to localStorage so other components on the page pick it up
        this.saveToStorage();
        this.notifyListeners('step-changed', this.data);

        return true;
      } catch (e) {
        console.warn('Could not load DYO state from URL:', e);
        return false;
      }
    }
    
    /**
     * Load DYO state from a metafield JSON object (embedded in Liquid).
     * Used as a fallback for visitors who have no localStorage or URL param data.
     * Does NOT save to localStorage — the visitor is just viewing, not designing.
     *
     * @param {Object} jsonData - Parsed JSON with { setting, diamond, createdProduct }
     * @param {string} productHandle - The product handle from the page (data-product-handle)
     * @returns {boolean} True if state was successfully loaded
     */
    loadFromMetafield(jsonData, productHandle) {
      try {
        if (!jsonData || (!jsonData.setting && !jsonData.diamond)) return false;

        this.data = {
          setting: jsonData.setting || null,
          diamond: jsonData.diamond || null,
          createdProduct: jsonData.createdProduct || null
        };

        // Infer createdProduct from the product handle if not set in the metafield
        // (buildProductPayload runs before setCreatedProduct, so it's always null)
        if (!this.data.createdProduct && productHandle && productHandle !== 'custom-ring') {
          this.data.createdProduct = {
            handle: productHandle,
            url: `/products/${productHandle}`
          };
        }

        // Mark steps as completed
        if (this.data.setting) this.steps[0].status = 'completed';
        if (this.data.diamond) this.steps[1].status = 'completed';

        // Save to localStorage so the data overrides any previous state
        this.saveToStorage();
        this.notifyListeners('step-changed', this.data);
        return true;
      } catch (e) {
        console.warn('Could not load DYO state from metafield:', e);
        return false;
      }
    }

    /**
     * Reset the entire configuration
     */
    reset() {
      this.startedWith = '';
      this.nextStep = '';
      this.data = { setting: null, diamond: null, createdProduct: null };

      this.steps = [
        { id: 'settings', name: 'Pick Setting', status: 'pending' },
        { id: 'diamonds', name: 'Pick Stone', status: 'pending' },
        { id: 'review', name: 'Review Your Ring', status: 'pending' }
      ];
      
      this.saveToStorage();
      this.notifyListeners('reset', null);
    }
    
    /**
     * Add event listener
     */
    addEventListener(callback) {
      this.listeners.push(callback);
    }
    
    /**
     * Remove event listener
     */
    removeEventListener(callback) {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    }
    
    /**
     * Notify all listeners of changes
     */
    notifyListeners(event, data) {
      this.listeners.forEach(callback => {
        try {
          callback(event, data);
        } catch (error) {
          console.error('DYO API listener error:', error);
        }
      });
    }
    
    /**
     * Save state to localStorage with 1-day expiration
     */
    saveToStorage() {

      try {

        const now = new Date().getTime();
        const expirationTime = now + (24 * 60 * 60 * 1000); // 1 day in milliseconds
        
        const state = {
          steps: this.steps,
          data: this.data,
          startedWith: this.startedWith,
          nextStep: this.nextStep,
          timestamp: now,
          expiresAt: expirationTime
        };

        localStorage.setItem('dyo-state', JSON.stringify(state));

      } catch (error) {

        console.warn('Could not save DYO state to localStorage:', error);
      }
    }
    
    /**
     * Load state from localStorage (with expiration check)
     */
    loadFromStorage() {

      try {

        const saved = localStorage.getItem('dyo-state');

        if (saved) {

          const state = JSON.parse(saved);
          const now = new Date().getTime();
          
          // Check if data has expired (older than 1 day)
          if (state.expiresAt && now > state.expiresAt) {

            localStorage.removeItem('dyo-state');

            // Don't load expired data
            return;
          }
          
          // Load valid data
          this.steps = state.steps || this.steps;
          this.data = state.data || this.data;
          this.startedWith = state.startedWith || '';
          this.nextStep = state.nextStep || '';
        }

      } catch (error) {
        
        console.warn('Could not load DYO state from localStorage:', error);

        // Clear corrupted data
        localStorage.removeItem('dyo-state');
      }
    }
    
    /**
     * Clear expired localStorage data manually
     */
    clearExpiredStorage() {
      try {
        const saved = localStorage.getItem('dyo-state');
        if (saved) {
          const state = JSON.parse(saved);
          const now = new Date().getTime();
          
          if (state.expiresAt && now > state.expiresAt) {
            localStorage.removeItem('dyo-state');
            return true; // Was expired
          }
        }
      } catch (error) {
        localStorage.removeItem('dyo-state');
      }
      return false; // Was not expired
    }

    /**
     * Check the compatibility of a product and a stone
     * 
     * @param {string} productHandle The handle of the product
     * @param {string} stoneId The id of the stone
     * @param {string} stoneType The type of the stone
     * 
     * @example
     * 
     * checkCompatibility('product-handle', 'stone-id', 'stone-type')
     * 
     * @param {*} productHandle 
     * @param {*} stoneId 
     * @returns {boolean} The compatibility of the product and the stone
     */
    async checkCompatibility(productHandle, stoneId, stoneType) {

      const product = await window.theme.product.loadProduct(productHandle);

      let stone;
      const cachedDiamond = this.data && this.data.diamond;
      if (cachedDiamond && cachedDiamond.source === 'shopify' && String(cachedDiamond.id) === String(stoneId)) {
        stone = { shape: cachedDiamond.shape, carat: cachedDiamond.carat };
      } else {
        stone = await window.theme.stones.getStoneById(stoneId, stoneType);
      }

      if (!stone || !stone.shape) return true;

      const reference = `${stone.shape.toLowerCase()}_min_carat_weight`;

      return parseFloat(product.compatibility[reference]) <= parseFloat(stone.carat);
    }

    /**
     * Get the final price of the setting considering the properties
     * 
     * @param {object} settingData The setting data
     * @example
     * 
     * getSettingFinalPrice({ price: 100, size: '12:100', warranty: '2:50', appraisal: 'true:99' })
     * 
     * @returns {number} The final price of the setting considering the properties
     */
    getSettingFinalPrice(settingData) {
      const { price, size, warranty, appraisal } = settingData;
      let finalPrice = parseFloat(price);
      
      if (size) {
        const extra = parseFloat(size.split(':')[1] || 0) * 100;
        finalPrice += extra;
      }
      
      if (warranty) {
        finalPrice += parseFloat(warranty.split(':')[1]) * 100;
      }

      if (appraisal && appraisal.split(':')[0] === 'true') {
        finalPrice += parseFloat(appraisal.split(':')[1]) * 100;
      }

      return finalPrice;
    }

    /**
     * Modify the setting data
     * 
     * @param {string} key The key to modify
     * @param {string} value The value to set
     * 
     * @example
     * 
     * modifySettingData('size', '12:100')
     * 
     * @returns {boolean} The success of the modification
     */
    modifySettingData(key, value) {

      this.data.setting[key] = value;

      // Calculate the final price
      this.data.setting.totalPrice = this.getSettingFinalPrice(this.data.setting);

      this.saveToStorage();
      this.notifyListeners('setting-modified', this.data.setting);

      return true;
    }

    /**
     * Build the product payload for the API
     */
    buildProductPayload() {
      const setting = this.data.setting;
      const diamond = this.data.diamond;
      const toAbsolute = (url) => url?.startsWith('//') ? `https:${url}` : (url || null);
      const totalPriceCents = (setting.totalPrice || 0) + (diamond.totalPrice || 0);
      const totalPrice = (totalPriceCents / 100).toFixed(2);
      const sku = 'DYO-CUSTOM-RING';
      // Build product-level metafields from setting's property metafields
      const listMetafields = ['size', 'warranty', 'backing', 'chain_length', 'pendant_initials', 'number_of_initials', 'monogram_char'];
      const stringMetafields = ['is_engravable'];
      const productMetafields = [];

      if (setting.metafields) {
        for (const key of listMetafields) {
          const val = setting.metafields[key];
          if (val !== undefined && val !== null && val !== '') {
            productMetafields.push({
              namespace: 'custom',
              key,
              value: JSON.stringify(Array.isArray(val) ? val : [val]),
              type: 'list.single_line_text_field'
            });
          }
        }
        for (const key of stringMetafields) {
          const val = setting.metafields[key];
          if (val !== undefined && val !== null && val !== '') {
            productMetafields.push({
              namespace: 'custom',
              key,
              value: String(val),
              type: 'single_line_text_field'
            });
          }
        }
      }

      return {
        title: `${setting.title} with ${diamond.title}`,
        productType: 'Final Design Your Own',
        tags: ['dyo', 'design-your-own'],
        status: 'UNLISTED',
        templateSuffix: 'dyo-custom-ring',
        metafields: productMetafields,
        variants: [{
          price: totalPrice,
          sku,
          metafields: [
            { namespace: 'custom', key: 'dyo_setting_variant_id', value: String(setting.id),  type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_stone_id',           value: String(diamond.id),  type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_setting_sku',        value: setting.sku || '',   type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_stone_sku',          value: diamond.sku || '',   type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_stone_source',       value: diamond.source || 'legacy', type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_stone_handle',       value: diamond.handle || '1', type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_stone_variant_id',   value: diamond.variantId ? String(diamond.variantId) : '1', type: 'single_line_text_field' },
            { namespace: 'custom', key: 'dyo_json',              value: JSON.stringify(this.data), type: 'json' }
          ]
        }],
        images: [
          ...(setting.images && setting.images.length > 0
            ? setting.images.map(img => ({ src: toAbsolute(img), alt: setting.title })).filter(i => i.src)
            : (toAbsolute(setting.image) ? [{ src: toAbsolute(setting.image), alt: setting.title }] : [])),
          ...(toAbsolute(diamond.image) ? [{ src: toAbsolute(diamond.image), alt: diamond.title }] : [])
        ],
        ...(toAbsolute(setting.image) && toAbsolute(diamond.image) ? {
          compositeImages: [{
            base: { src: toAbsolute(setting.image), alt: setting.title },
            overlay: { src: toAbsolute(diamond.image), position: 'bottom-right', size: 0.5 },
            alt: `${setting.title} with ${diamond.title}`
          }]
        } : {})
      };
    }

    /**
     * Store the API-created product reference in DYO state
     * @param {Object} productData - The product data returned from createProduct()
     */
    setCreatedProduct(productData) {
      this.data.createdProduct = {
        handle: productData.handle,
        url: `/products/${productData.handle}`
      };
      this.saveToStorage();
    }

    /**
     * Create a real Shopify product via the API and return its data
     */
    async createProduct() {
      if (!this.isComplete()) throw new Error('DYO configuration is incomplete');
      const response = await fetch('https://app.allurez.com/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(this.buildProductPayload())
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Product creation failed');
      return result.data;
    }
  }

  // Create global instance under theme namespace
  window.theme = window.theme || {};
  window.theme.dyo = window.theme.dyo || new DYOApi();

}